// src/contexts/FilterContext.tsx

import React, { createContext, useState, useContext, ReactNode} from 'react';

// Define the shape of the filter state
interface Colors {
    orange: string;
    white: string;
    black: string;
    blue: string;
    green: string;
    lightRed: string;
    red: string;
    darkRed: string;
    lightGrey: string;
    grey: string;
    blurredBlue: string;
    lightBlack: string;
    tableHeaderBlue: string;
    lightWhite: string;
}

interface SnackbarPosition{
    vertical: 'top' | 'bottom';
    horizontal:  'left' | 'right';
}

interface Spacing{
    none: string;
    xs: string,
    sm: string,
    md: string,
    lg: string,
    xl: string
}

interface Fonts{
    pageTitle: string;
    title: string;
    description: string;
    pageSectionTitle: string;
    value: string;
    subParagraphText: string;
    subParagraphTitle: string;
}

interface BorderRadius{
    md: string;
    sm: string;
    xl: string;
}

interface BorderWidth{
    thin: string;
    medium: string;
    hard: string;
}

interface BorderType{
    solid: string;
}

interface Border{
    radius: BorderRadius;
    width: BorderWidth;
    type: BorderType;
}

interface ThemeProps {
    palette: Colors;
    snackbarPosition: SnackbarPosition;
    border: Border;
    spacing: Spacing;
    fontWeights: Fonts;
    fontSizes: Fonts;
    dateFormat: DateFormat
}

interface DateFormat{
    datePicker: string
    dateRange: string
    label : string
}

const ThemeContext = createContext<ThemeProps | undefined>(undefined);

const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [theme] = useState<ThemeProps>(
        {
            snackbarPosition: { vertical: 'top', horizontal: 'left' },
            border: {radius: {md:'10px', sm:'5px', xl:'25px'}, width: {thin:'1px', medium:'2px', hard: '5px'}, type: {solid:'solid'}},
            spacing: {none: '0px', xs: '5px', sm: '10px', md: '20px', lg: '50px', xl: '100px'},
            fontWeights: {pageTitle: 'bold', title: 'bold', description: '700', pageSectionTitle: 'bold', value:'bold', subParagraphTitle: 'bold', subParagraphText: '700'},
            fontSizes: {pageTitle: '28px',title: '22px', description:'13px', pageSectionTitle: '20px', subParagraphText: '10px', subParagraphTitle: '14px', value: '10px'},
            palette: {
                orange: '#ff9c00',
                white:'white',
                black: 'black',
                lightBlack: 'rgba(0,0,0,0.7)',
                blue: 'rgb(13,24,32)',
                lightGrey: 'silver',
                green: 'green',
                lightRed: 'rgba(255, 0, 0, 0.5)',
                red: 'red',
                darkRed:'rgba(150, 0, 0, 0.7)',
                blurredBlue: 'rgba(13,24,32, 0.8)',
                grey:'rgba(80, 80, 80)',
                tableHeaderBlue: 'hsl(203, 45%, 10%)',
                lightWhite: 'rgb(150, 150, 150, 0.3)'
            },
            dateFormat: {dateRange: 'DD/MM/YYYY', datePicker: 'DD/MM/YYYY HH:mm', label: 'DD/MM/YYYY HH:mm'}
        }
    );

    return (
        <ThemeContext.Provider value={theme}>
            {children}
        </ThemeContext.Provider>
    );
};

const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export { ThemeProvider, useTheme };